import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Image,
  Row,
  Space,
  Spin,
  Typography,
  theme,
} from "antd";
import "./NurseMedicine.css";
import { getNurseCommonData } from "../../../services/nurse.services";
import useQuery from "../../../hooks/useQuery";
import inputStyles from "../../../styles/Custom/Input.module.css";
import StatMedicines, { StatSosSummaryTable } from "./StatMedicines";
import ScheduledMedicine from "./ScheduledMedicine";
import calenderIcon from "../../../assets/images/nurseIcons/calender.svg";
import NoDataPage from "../../NoDataPages/NoDataPage";
import medicineBoxIcon from "../../../assets/images/nurseIcons/medicinebox.svg";
import LoadingIndicator from "../../Loaders/LoadingIndicator";
import dayjs from "dayjs";
import MedicationHistoryChart from "../MedicationsIp/MedicationHistoryChart";
import useReceptionistMobilePickerProps from "../../Receptionist/receptionistMobilePickerProps";
const dateFormat = "YYYY-MM-DD";

const TILE_W = 170;
const TILE_GAP = 12;

const filterScheduledMedicinesForNurse = (list = []) =>
  (Array.isArray(list) ? list : []).filter((medicine) =>
    (medicine?.actions || []).some((a) => a?.is_stopped !== true)
  );

const extractActiveScheduledTimeSlots = (data = []) => [
  ...new Set(
    data.flatMap((medicine) =>
      (medicine?.actions || [])
        .filter((a) => a?.is_stopped !== true)
        .map((action) => action.hour)
    )
  ),
];

const statTimingsMinWidth = (statData) => {
  const rows = Array.isArray(statData) ? statData : [];
  const maxNurseTimings = rows.reduce((max, med) => {
    const count = (med?.actions || []).reduce((acc, a) => {
      if (Array.isArray(a?.nurse_data)) return acc + a.nurse_data.length;
      return acc;
    }, 0);
    return Math.max(max, count);
  }, 0);
  if (!maxNurseTimings) return 520;
  const contentW =
    maxNurseTimings * TILE_W + Math.max(0, maxNurseTimings - 1) * TILE_GAP;
  return Math.max(520, contentW + 24);
};

const NurseMedicine = ({ patientData }) => {
  const { token } = theme.useToken();
  const mobilePickerProps = useReceptionistMobilePickerProps();
  const curDate = dayjs().format(dateFormat);
  const params = useQuery();
  const [medData, setMedData] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [date, setDate] = useState(curDate);
  const [statData, setStatData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyModal, setHistoryModal] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const res = await getNurseCommonData(
          params?.patient,
          "medication",
          date
        );
        setMedData(
          res?.data?.data?.filter(
            (data) =>
              data?.frequency !== "If_Required" &&
              data?.frequency !== "Immediately"
          )
        );
        setStatData(
          res?.data?.data?.filter(
            (data) =>
              data?.frequency === "If_Required" ||
              data?.frequency === "Immediately"
          )
        );
        setLoading(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refresh, date]);

  const refreshPage = () => {
    setRefresh((prev) => !prev);
  };

  const disabledDate = (current) => current && current > dayjs().endOf("day");

  const onDateChange = (date, dateString) => {
    setDate(date ? dateString : null);
  };

  const scheduledMedDataForTable = useMemo(
    () => filterScheduledMedicinesForNurse(medData),
    [medData]
  );

  const statDataForDisplay = useMemo(() => statData, [statData]);

  const sortedTimeSlots = useMemo(() => {
    const slots =
      scheduledMedDataForTable.length > 0
        ? extractActiveScheduledTimeSlots(scheduledMedDataForTable)
        : [];
    return [...slots].sort((a, b) => a - b);
  }, [scheduledMedDataForTable]);

  const timingsRegionWidth = useMemo(() => {
    const statMin = statTimingsMinWidth(statDataForDisplay);
    const n = sortedTimeSlots?.length ?? 0;
    const schedMin = n * TILE_W;
    return Math.max(statMin, schedMin);
  }, [statDataForDisplay, sortedTimeSlots]);

  const hasScheduledRowsToShow = scheduledMedDataForTable.length > 0;
  const hasAnyMedicationToShow =
    hasScheduledRowsToShow || statDataForDisplay?.length > 0;

  return (
    <div className="nurse-medicine-page">
      <div className="nurse-medicine-toolbar">
        <div className="nurse-medicine-toolbar__filters">
          <Space className="nurse-medicine-toolbar__filters-space" size={8}>
            <DatePicker
              style={{
                border: `1px solid ${token.colorPrimary}`,
                height: "38px",
                width: "150px",
              }}
              disabledDate={disabledDate}
              className={`nurse-medicine-date-picker ${inputStyles.mdDateInput}`}
              onChange={onDateChange}
              defaultValue={dayjs(curDate)}
              allowClear={false}
              suffixIcon={<Image width={20} src={calenderIcon} preview={false} />}
              {...mobilePickerProps}
            />
            <Button
              className="inv-btn nurse-medicine-history-btn"
              style={{ height: "38px" }}
              type="primary"
              onClick={() => setHistoryModal(true)}
            >
              Medication History
            </Button>
          </Space>
        </div>

        {statDataForDisplay?.length > 0 && (
          <div className="nurse-medicine-toolbar__stat">
            <StatMedicines
              statData={statDataForDisplay}
              refreshPage={refreshPage}
              variant="headerButtons"
            />
          </div>
        )}
      </div>

      {!loading && statDataForDisplay?.length > 0 && (
        <div className="nurse-med-table-wrap" style={{ marginBottom: 10 }}>
          <StatSosSummaryTable
            statData={statDataForDisplay}
            timingsRegionWidth={timingsRegionWidth}
          />
        </div>
      )}

      {/* {statData?.length > 0 && (
        <StatMedicines statData={statData} refreshPage={refreshPage} />
      )} */}
      {loading ? (
        <LoadingIndicator message={"Loading medication data, please wait..."} />
      ) : hasAnyMedicationToShow ? (
        <>
          <div className="nurse-med-table-wrap">
            <ScheduledMedicine
              medData={scheduledMedDataForTable}
              statData={statDataForDisplay}
              sortedTimeSlots={sortedTimeSlots}
              refreshPage={refreshPage}
              date={date}
              curDate={curDate}
              timingsRegionWidth={timingsRegionWidth}
            />
          </div>
          {historyModal && (
            <MedicationHistoryChart
              open={historyModal}
              setOpen={setHistoryModal}
              data={[...medData, ...statData]}
              params={params}
              patientData={patientData?.admission}
            />
          )}
        </>
      ) : (
        <Row
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "70vh",
          }}
        >
          <Col span={24} style={{ textAlign: "center" }}> 
            <NoDataPage
              title="No Medications Found"
              subtitle="No medications found for the selected date."
              image={medicineBoxIcon}
            />
          </Col>
        </Row>
      )}
    </div>
  );
};

export default NurseMedicine;
